import { forwardRef, type ReactNode } from "react"
import { useTranslation } from "react-i18next"
import { Badge } from "@/components/ui/badge"
import { Spinner } from "@/components/ui/spinner"
import { cn } from "@/lib/utils"
import type { OverviewStop } from "@/lib/driverOverview"

export const STOP_ROW = "flex h-9 min-w-0 items-center gap-3 pl-5 text-sm"

export const StopRowShell = forwardRef<
  HTMLLIElement,
  { stop: OverviewStop; className?: string; children: ReactNode }
>(({ stop, className, children }, ref) => (
  <li
    ref={ref}
    data-testid="driver-overview-stop"
    data-current={stop.current}
    data-alert={stop.alert}
    className={cn(
      STOP_ROW,
      stop.done && "text-muted-foreground",
      stop.alert && "text-red-600 dark:text-red-400",
      className,
    )}
  >
    {children}
  </li>
))
StopRowShell.displayName = "StopRowShell"

export const StopLabel = ({ stop }: { stop: OverviewStop }) => (
  <>
    <span
      className={cn(
        !stop.alert && "text-foreground",
        stop.current && "font-medium",
      )}
    >
      {stop.name}
    </span>
    {stop.address && (
      <span className={cn(!stop.alert && "text-muted-foreground")}>
        {" "}
        · {stop.address}
      </span>
    )}
  </>
)

const ETA_TONE = {
  unplanned: "text-muted-foreground",
  onTime: "text-green-800 dark:text-green-500",
  late: "text-red-500",
} as const

export const StopStatus = ({ stop }: { stop: OverviewStop }) => {
  const { t } = useTranslation()
  return (
    <>
      {stop.eta && (
        <span
          data-testid="driver-overview-stop-eta"
          data-tone={stop.eta.tone}
          title={
            stop.eta.tone === "late"
              ? t("chat.overviewStops.lateBy", { delay: stop.eta.delay })
              : undefined
          }
          className={cn(
            "shrink-0 whitespace-nowrap tabular-nums",
            ETA_TONE[stop.eta.tone],
          )}
        >
          {stop.eta.tone === "late"
            ? t("chat.overviewStops.etaDelayed", { eta: stop.eta.label })
            : stop.eta.label}
        </span>
      )}
      {stop.distance && (
        <Badge
          variant={stop.distance.tone}
          data-testid="driver-overview-stop-distance"
          data-tone={stop.distance.tone}
          className="shrink-0 whitespace-nowrap"
        >
          {stop.distance.label}
        </Badge>
      )}
    </>
  )
}

/** A stop edit the backend hasn't confirmed yet. */
export const PendingStop = ({
  label,
  verb,
}: {
  label: string
  verb: "Saving" | "Removing"
}) => {
  const { t } = useTranslation()
  return (
    <>
      <Spinner size="xs" className="shrink-0 text-muted-foreground" />
      <span
        role="status"
        aria-label={
          verb === "Saving"
            ? t("chat.overviewStops.saving", { label })
            : t("chat.overviewStops.removing", { label })
        }
        className="min-w-0 flex-1 truncate text-muted-foreground"
      >
        {label}
      </span>
    </>
  )
}
